import * as React from "react";
import { TitleSectionFormRegistration } from "../../../FormRegistration/components/TitleSection";
import TextInputFormRegistration from "../../../FormRegistration/components/TextInput/TextInput";
import { useRegistrationFormDance } from "../../../../../store/user";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import {
  checkFilledData,
  getStringSafely,
  handleIsRequired,
  requiredDefaultMessage,
  validateEmail,
  validatePhoneNumber,
} from "../../../FormRegistration/utils";
import useWindowSize from "../../../../../hooks/useWindowSize";
import { FileUploadFormRegistration } from "../../../FormRegistration/components/FileUpload";

interface Props {
  isLoading: boolean;
  setStep: (step: number) => void;
}

const StepDataSekolah: React.FC<Props> = ({ isLoading = false, setStep }) => {
  const { size } = useWindowSize();
  // @ts-ignore
  const isDesktopView = (size?.width || 0) >= 768;

  const { category, mode } = useParams();
  const { dataSchool, errorSchool, setErrorSchool, setDataSchool } = useRegistrationFormDance();

  const isFormValid = () => {
    return (
      checkFilledData(dataSchool) >= 7 &&
      !validatePhoneNumber(dataSchool?.contact_person_number ?? "")
    );
  };

  const isEditMode = mode === "edit";
  const isButtonDisabled = isEditMode ? false : !isFormValid();

  const handleChangeInput = (value: string, name: string) => {
    setErrorSchool({ ...errorSchool, [name]: "" });
    setDataSchool({ ...dataSchool, [name]: value });
  };

  const handleNextStep = () => {
    if (isFormValid()) {
      return setStep(2);
    }
    setErrorSchool({
      school_name: handleIsRequired(getStringSafely(dataSchool?.school_name)),
      school_address: handleIsRequired(getStringSafely(dataSchool?.school_address)),
      contact_person_name: handleIsRequired(getStringSafely(dataSchool?.contact_person_name)),
      contact_person_number: validatePhoneNumber(
        getStringSafely(dataSchool?.contact_person_number),
      ),
      npsn_number: handleIsRequired(getStringSafely(dataSchool?.npsn_number)),
      team_social_media: handleIsRequired(getStringSafely(dataSchool?.team_social_media)),
      video_url: handleIsRequired(getStringSafely(dataSchool?.video_url)),
    });
  };

  const handleBackStep = () => {
    setStep(0);
  };

  const handleBlur = (_, error: string, name: string) => {
    setErrorSchool({ ...errorSchool, [name]: error });
  };

  return (
    <div
      className={classNames("h-auto overflow-y-auto pb-[60px]", {
        "pb-[20px]": isDesktopView,
      })}
    >
      <TitleSectionFormRegistration
        title="Masukan Data Sekolah"
        category={String(category).toUpperCase()}
        chipColor="#F48120"
        chipText="Dance"
        isDance
      />

      <div className="flex flex-col gap-y-[8px]">
        <TextInputFormRegistration
          label="Nama Sekolah"
          name="school_name"
          isRequired
          value={dataSchool?.school_name ?? ""}
          placeholder="Masukin nama sekolah kamu"
          errorMessage={errorSchool?.school_name ?? ""}
          type="text"
          onBlur={handleBlur}
          onChangeValue={handleChangeInput}
        />
        <TextInputFormRegistration
          label="Alamat Lengkap Sekolah"
          name="school_address"
          isRequired
          value={dataSchool?.school_address ?? ""}
          placeholder="Contoh: Jl.Piala by.U No.1-3, Kota Jakarta Selatan"
          errorMessage={errorSchool?.school_address ?? ""}
          type="text"
          onBlur={handleBlur}
          onChangeValue={handleChangeInput}
        />
        {/* TODO: add description for npsn */}
        <TextInputFormRegistration
          label="Nomer NPSN"
          name="npsn_number"
          description="Nomer Pokok Sekolah Nasional"
          isRequired
          value={dataSchool?.npsn_number ?? ""}
          placeholder="Masukin NPSN sekolah kamu"
          errorMessage={errorSchool?.npsn_number ?? ""}
          type="number"
          onBlur={handleBlur}
          onChangeValue={handleChangeInput}
        />
        <TextInputFormRegistration
          label="Nama Contact Person Sekolah / Pelatih"
          name="contact_person_name"
          isRequired
          value={dataSchool?.contact_person_name ?? ""}
          placeholder="Masukin nama contact person"
          errorMessage={errorSchool?.contact_person_name ?? ""}
          type="text"
          onChangeValue={handleChangeInput}
        />
        <TextInputFormRegistration
          label="Nomor Contact Person Sekolah / Pelatih"
          name="contact_person_number"
          isRequired
          value={dataSchool?.contact_person_number ?? ""}
          placeholder="Masukin nomor contact person"
          errorMessage={errorSchool?.contact_person_number ?? ""}
          type="number"
          onChangeValue={handleChangeInput}
          checkingValidationType="onTyping"
          maxLength={14}
          onBlur={handleBlur}
          validation={["phone_number"]}
        />
        <TextInputFormRegistration
          label="Social Media Tim Dance"
          name="team_social_media"
          isRequired
          value={dataSchool?.team_social_media ?? ""}
          placeholder="Masukin username instagram tim dance kamu"
          errorMessage={errorSchool?.team_social_media ?? ""}
          type="text"
          onChangeValue={handleChangeInput}
        />
        <TextInputFormRegistration
          label="Video Dance Tim Kamu"
          description="Masukan URL video dance tim kamu"
          name="video_url"
          isRequired
          value={dataSchool?.video_url ?? ""}
          placeholder="Contoh : https://youtu.be/GRde7WGScrM?si=S5EN5SfoiB5MpqBV"
          errorMessage={errorSchool?.video_url ?? ""}
          type="text"
          onChangeValue={handleChangeInput}
        />
      </div>

      <div className="w-full max-w-[600px] flex px-[24px] py-[16px] gap-x-[16px]">
        <button
          disabled={isLoading}
          onClick={handleBackStep}
          className="rounded-[32px] border-[1px] border-[#1F2D3D] py-[12px] px-[16px] text-[16px] text-[#1F2D3D] leading-none font-semibold w-[50%]"
        >
          Kembali
        </button>
        <button
          disabled={isLoading}
          onClick={handleNextStep}
          className={classNames(
            "rounded-[32px] bg-[#00BCF1] py-[12px] px-[16px] text-[16px] leading-none font-semibold w-[50%] text-white",
            {
              "bg-[#00BCF1]": !isButtonDisabled,
              "bg-slate-300": isButtonDisabled,
            },
          )}
        >
          Lanjut
        </button>
      </div>
    </div>
  );
};

export default StepDataSekolah;
